import React, { useMemo, useState } from 'react';

/**
 * Channel occupancy — the canonical wireless view.
 *
 * Each access point is drawn as a bell spanning its channel width, apex at its
 * measured signal. Overlap is the point: co-channel contention, a rogue AP
 * sitting on top of a corporate one, and which band the target actually lives
 * on are all immediately visible, and none of them are legible from a table.
 *
 * Signal is plotted on the real dBm axis rather than a normalised 0-100, so the
 * vertical position means something to anyone who works with RF.
 */

const FLOOR_DBM = -95;
const CEIL_DBM = -30;

// 802.11 channel centre frequencies. 2.4GHz channels overlap by design, which
// is why they are drawn on a frequency axis rather than an index.
const centreFreq = (channel, band) => {
  if (band === '2.4') {
    if (channel === 14) return 2484;
    return 2412 + (channel - 1) * 5;
  }
  return 5000 + channel * 5;
};

const BANDS = [
  { id: '2.4', label: '2.4 GHz', from: 2400, to: 2500, ticks: [1, 3, 6, 9, 11, 13] },
  { id: '5', label: '5 GHz', from: 5150, to: 5850, ticks: [36, 48, 60, 100, 120, 140, 157] },
];

// Colour carries meaning, not decoration: an open network and an enterprise
// network are different findings, and the eye should separate them before the
// analyst reads a single label.
const securityStyle = (authSuite) => {
  switch (authSuite) {
    case 'OPEN':
      return { stroke: '#fbbf24', fill: 'rgba(251,191,36,0.14)', label: 'Open' };
    case 'SAE':
      return { stroke: '#34d399', fill: 'rgba(52,211,153,0.14)', label: 'WPA3' };
    case '802.1X':
      return { stroke: '#a78bfa', fill: 'rgba(167,139,250,0.14)', label: 'Enterprise' };
    case 'PSK':
      return { stroke: '#818cf8', fill: 'rgba(129,140,248,0.12)', label: 'WPA2' };
    default:
      return { stroke: '#64748b', fill: 'rgba(100,116,139,0.10)', label: 'Unknown' };
  }
};

const VIEW_W = 1000;
const VIEW_H = 220;
const PAD = { top: 14, right: 12, bottom: 26, left: 40 };

const ChannelSpectrum = ({ accessPoints = [], highlightBssid, onSelect }) => {
  const [hovered, setHovered] = useState(null);

  const grouped = useMemo(() => {
    const out = { '2.4': [], '5': [] };
    accessPoints.forEach((ap) => {
      if (!ap.channel || !ap.band) return;
      if (out[ap.band]) out[ap.band].push(ap);
    });
    // Weakest first so strong APs draw on top and stay readable.
    Object.values(out).forEach((list) =>
      list.sort((a, b) => (a.signal_dbm ?? -100) - (b.signal_dbm ?? -100)));
    return out;
  }, [accessPoints]);

  const yFor = (dbm) => {
    const clamped = Math.max(FLOOR_DBM, Math.min(CEIL_DBM, dbm ?? FLOOR_DBM));
    const ratio = (clamped - FLOOR_DBM) / (CEIL_DBM - FLOOR_DBM);
    return PAD.top + (1 - ratio) * (VIEW_H - PAD.top - PAD.bottom);
  };

  const renderBand = (band) => {
    const aps = grouped[band.id] || [];
    const span = band.to - band.from;
    const xFor = (freq) =>
      PAD.left + ((freq - band.from) / span) * (VIEW_W - PAD.left - PAD.right);
    const floorY = yFor(FLOOR_DBM);

    return (
      <div key={band.id} className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between mb-1 px-1">
          <span className="text-xs font-medium text-slate-400">{band.label}</span>
          <span className="text-[11px] text-slate-600 tabular-nums">
            {aps.length} {aps.length === 1 ? 'network' : 'networks'}
          </span>
        </div>
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="w-full h-[200px] select-none"
          preserveAspectRatio="none"
        >
          {/* dBm gridlines — the axis an RF engineer reads */}
          {[-40, -50, -60, -70, -80, -90].map((dbm) => (
            <g key={dbm}>
              <line
                x1={PAD.left} x2={VIEW_W - PAD.right}
                y1={yFor(dbm)} y2={yFor(dbm)}
                className="stroke-slate-800" strokeWidth="1"
                strokeDasharray={dbm === -70 ? '0' : '3 5'}
              />
              <text
                x={PAD.left - 6} y={yFor(dbm) + 3}
                textAnchor="end"
                className="fill-slate-600"
                style={{ fontSize: '9px' }}
              >
                {dbm}
              </text>
            </g>
          ))}

          {/* Channel ticks */}
          {band.ticks.map((ch) => {
            const x = xFor(centreFreq(ch, band.id));
            return (
              <g key={ch}>
                <line x1={x} x2={x} y1={floorY} y2={floorY + 4} className="stroke-slate-700" />
                <text
                  x={x} y={floorY + 15} textAnchor="middle"
                  className="fill-slate-600" style={{ fontSize: '9px' }}
                >
                  {ch}
                </text>
              </g>
            );
          })}
          <line
            x1={PAD.left} x2={VIEW_W - PAD.right} y1={floorY} y2={floorY}
            className="stroke-slate-700"
          />

          {/* One bell per access point, 20 MHz wide */}
          {aps.map((ap) => {
            const freq = centreFreq(ap.channel, band.id);
            const style = securityStyle(ap.auth_suite);
            const cx = xFor(freq);
            const left = xFor(freq - 11);
            const right = xFor(freq + 11);
            const top = yFor(ap.signal_dbm);
            const isActive = hovered === ap.bssid || highlightBssid === ap.bssid;
            const dimmed = (hovered || highlightBssid) && !isActive;

            return (
              <g
                key={ap.bssid}
                onMouseEnter={() => setHovered(ap.bssid)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect && onSelect(ap.bssid)}
                style={{ cursor: onSelect ? 'pointer' : 'default' }}
                opacity={dimmed ? 0.25 : 1}
                className="transition-opacity duration-200"
              >
                <path
                  d={`M ${left} ${floorY} Q ${cx} ${top - 18} ${right} ${floorY} Z`}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={isActive ? 2 : 1.25}
                  className="transition-all duration-500 ease-out"
                />
                {/* Label only what is legible: strong, or hovered */}
                {(isActive || (ap.signal_dbm ?? -100) > -65) && (
                  <text
                    x={cx}
                    y={top - 22}
                    textAnchor="middle"
                    className="fill-slate-300"
                    style={{ fontSize: '10px', fontWeight: isActive ? 600 : 400 }}
                  >
                    {ap.hidden ? '‹hidden›' : (ap.ssid || '').slice(0, 22)}
                  </text>
                )}
              </g>
            );
          })}

          {aps.length === 0 && (
            <text
              x={VIEW_W / 2} y={VIEW_H / 2} textAnchor="middle"
              className="fill-slate-700" style={{ fontSize: '11px' }}
            >
              nothing observed on this band
            </text>
          )}
        </svg>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row gap-6">
        {BANDS.map(renderBand)}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 px-1">
        {['OPEN', 'PSK', 'SAE', '802.1X'].map((suite) => {
          const style = securityStyle(suite);
          return (
            <span key={suite} className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <span
                className="inline-block w-3 h-[3px] rounded-full"
                style={{ backgroundColor: style.stroke }}
              />
              {style.label}
            </span>
          );
        })}
        <span className="text-[11px] text-slate-600 ml-auto">
          Height is measured signal · width is 20 MHz channel occupancy
        </span>
      </div>
    </div>
  );
};

export default ChannelSpectrum;
